import { useContext, useEffect, useRef } from "react";
import dynamic from "next/dynamic";
import { TrustpilotConfig } from "@/core/types/types";
import { getTrustPilotConfiguration } from "@/utils";
import { ConfigContext } from "@/core/contexts/ConfigContext";
import { InitialisationConfig } from "@/core/utils/getInitialisationConfig";

declare global {
  interface Window {
    Trustpilot: any;
  }
}

type TrustpilotProps = {
  isSidebar?: boolean;
};

const Trustpilot = function ({ isSidebar = false }: TrustpilotProps) {
  const ref = useRef(null);
  const initialisationConfig = useContext<InitialisationConfig>(ConfigContext);
  const configuration = getTrustPilotConfiguration(initialisationConfig);

  // Trustpilot rendering approach
  // https://support.trustpilot.com/hc/en-us/articles/115011421468--Add-a-TrustBox-widget-to-a-single-page-application
  useEffect(() => {
    if (window.Trustpilot && ref.current) {
      window.Trustpilot.loadFromElement(ref.current, true);
    }
  }, [ref]);

  if (!configuration) return null;

  const currentConfig: TrustpilotConfig = isSidebar
    ? configuration.sidebar
    : configuration.header;

  const {
    className = "trustpilot-widget",
    locale,
    trustpilotTemplateId,
    trustpilotBusinessUnitId,
    trustpilotReviewsURL,
    trustpilotLinkTitle,
    theme,
    height,
    width,
  } = currentConfig;

  return (
    <>
      {trustpilotBusinessUnitId ? (
        <div
          ref={ref}
          className={className}
          data-locale={locale}
          data-template-id={trustpilotTemplateId}
          data-businessunit-id={trustpilotBusinessUnitId}
          data-style-height={height}
          data-style-width={width}
          data-theme={theme}
        >
          <a
            href={trustpilotReviewsURL}
            target="_blank"
            rel="noopener"
            title={trustpilotLinkTitle}
          ></a>
        </div>
      ) : null}
    </>
  );
};

export default dynamic(() => Promise.resolve(Trustpilot), { ssr: false });
